import { useCallback, useRef } from 'react';

import { AppIcon, AppIconProps } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import {
  BlueprintAspirationsCategories,
  useFetchAspirationsCategoriesQuery,
} from '@backrs_ui/grow-ui/src/redux/blueprint/blueprint.lubav.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  selectSharedBlueprintData,
  selectSharedSessionOwnerFullName,
  selectSharedSessionOwnerProfileImageUrl,
} from '@backrs_ui/grow-ui/src/redux/sharedSession/sharedSession';
import { selectUser, selectProfilePhoto } from '@backrs_ui/grow-ui/src/redux/user/user';
import {
  selectAspirationsCategories,
  selectMostRecentUserResponseSet,
} from '@backrs_ui/grow-ui/src/redux/userResponse/userResponse';

import { Avatar, Box, Typography, SxProps, Theme } from '@mui/material';

import { AspirationCard, AspirationActionCard, ASPIRATION_CARD_DATA_ATTR } from './AspirationCard';
import { useAgentContext } from './AspirationCardGrid.agentContext';
import { ConnectingLines, LineSegment } from './ConnectingLines';

const STRINGS = appStrings.modules.survey.output.aspirationCategories;

const COL_GROUP_TOLERANCE = 20;
const AVATAR_OUTLINE_CLEARANCE = 4;
const BADGE_CENTER_OFFSET = 28;
const SPINE_CARD_GAP = 16;

type AspirationCardGridProps = Readonly<{
  isShared?: boolean;
  onStartOver?: () => void;
}>;

export function AspirationCardGrid({ isShared = false, onStartOver }: AspirationCardGridProps) {
  const user = useAppSelector(selectUser);
  const profilePhoto = useAppSelector(selectProfilePhoto);
  const mostRecentUserResponseSet = useAppSelector(selectMostRecentUserResponseSet);
  const aspirationsCategories = useAppSelector(selectAspirationsCategories);
  const sharedBlueprintData = useAppSelector(selectSharedBlueprintData);
  const sharedOwnerFullName = useAppSelector(selectSharedSessionOwnerFullName);
  const ownerProfileImageUrl = useAppSelector(selectSharedSessionOwnerProfileImageUrl);

  // Only fetch if we don't already have categories in Redux
  const shouldFetch = !isShared && !aspirationsCategories && !!mostRecentUserResponseSet?.id;
  const { data: fetchedCategories, isFetching } = useFetchAspirationsCategoriesQuery(
    { response_set_id: mostRecentUserResponseSet?.id || '' },
    { skip: !shouldFetch },
  );

  // For shared views, categories come from shared blueprint data
  const currentCategories = isShared
    ? sharedBlueprintData?.aspirationsCategories
    : aspirationsCategories || fetchedCategories;

  const displayName = isShared
    ? (sharedOwnerFullName ?? '')
    : `${user?.first_name || ''} ${user?.last_name || ''}`.trim();

  const displayPhoto = isShared ? ownerProfileImageUrl : profilePhoto;

  const cards = buildCards(isFetching ? undefined : currentCategories);

  useAgentContext(currentCategories);

  const containerRef = useRef<HTMLDivElement>(null);
  const avatarRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const calculateLines = useCallback((container: HTMLDivElement): LineSegment[] => {
    const avatar = avatarRef.current;
    const grid = gridRef.current;

    if (!avatar || !grid) return [];

    const containerRect = container.getBoundingClientRect();
    const avatarRect = avatar.getBoundingClientRect();
    const cardEls = (Array.from(grid.children) as HTMLElement[]).filter(
      (el) => el.getAttribute(ASPIRATION_CARD_DATA_ATTR) === 'true',
    );

    if (cardEls.length === 0) return [];

    const toRel = (rect: DOMRect) => ({
      top: rect.top - containerRect.top,
      left: rect.left - containerRect.left,
      bottom: rect.bottom - containerRect.top,
      centerX: rect.left + rect.width / 2 - containerRect.left,
    });

    const avatarPos = toRel(avatarRect);
    const cardPositions = cardEls.map((el) => toRel(el.getBoundingClientRect()));

    // Group cards into columns by similar X position
    const columns: { x: number; cards: typeof cardPositions }[] = [];

    for (const card of cardPositions) {
      const existing = columns.find((c) => Math.abs(c.x - card.left) < COL_GROUP_TOLERANCE);

      if (existing) {
        existing.cards.push(card);
      } else {
        columns.push({ x: card.left, cards: [card] });
      }
    }

    columns.sort((a, b) => a.x - b.x);

    const firstCardTop = Math.min(...cardPositions.map((p) => p.top));
    const spineY = firstCardTop - SPINE_CARD_GAP;
    const trunkX = avatarPos.centerX;
    const segments: LineSegment[] = [];

    if (columns.length === 1) {
      const lastCardTop = Math.max(...cardPositions.map((p) => p.top));

      segments.push({
        x1: trunkX,
        y1: avatarPos.bottom + AVATAR_OUTLINE_CLEARANCE,
        x2: trunkX,
        y2: lastCardTop,
      });
    } else {
      const colDropXs = columns.map((c) => c.x + BADGE_CENTER_OFFSET);
      const firstColLastCardTop = Math.max(...columns[0].cards.map((c) => c.top));

      segments.push({
        x1: trunkX,
        y1: avatarPos.bottom + AVATAR_OUTLINE_CLEARANCE,
        x2: trunkX,
        y2: firstColLastCardTop,
      });

      segments.push({
        x1: trunkX,
        y1: spineY,
        x2: colDropXs[colDropXs.length - 1],
        y2: spineY,
      });

      for (let i = 1; i < columns.length; i++) {
        const lastCardTop = Math.max(...columns[i].cards.map((c) => c.top));

        segments.push({ x1: colDropXs[i], y1: spineY, x2: colDropXs[i], y2: lastCardTop });
      }
    }

    return segments;
  }, []);

  return (
    <Box ref={containerRef} sx={containerSx}>
      {/* Header */}
      <Box sx={headerSx}>
        <Box sx={profileRowSx}>
          <Avatar ref={avatarRef} src={displayPhoto || undefined} sx={avatarSx}>
            {displayName?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="display-xs-semibold" sx={sectionTitleSx}>
              {STRINGS.sectionTitle}
            </Typography>
            {displayName && (
              <Typography
                variant="text-md-medium"
                color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
              >
                {displayName}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <ConnectingLines containerRef={containerRef} calculate={calculateLines} />

      {/* Card Grid */}
      <Box ref={gridRef} sx={gridSx}>
        {cards.map((card) => (
          <AspirationCard
            key={card.key}
            icon={card.icon}
            title={card.title}
            description={card.description}
          />
        ))}
        {onStartOver && (
          <AspirationActionCard
            buttonId="blueprint-take-it-again-button"
            prompt={STRINGS.notSurePrompt}
            buttonLabel={STRINGS.takeItAgain}
            buttonIconName="ReverseRight"
            onClick={onStartOver}
          />
        )}
      </Box>

      <Box sx={lubavButtonWrapperSx}>
        <LubavButton
          id="blueprint-aspirations-discuss-btn"
          label={STRINGS.discussLabel}
          chatMessage="Discuss my summary of life aspirations"
          promptButtonId="blueprint_aspirations"
          buttonSize="md"
        />
      </Box>
    </Box>
  );
}

// --- Static Data ---

const cardIcon = (name: AppIconProps['iconName']) => (
  <AppIcon iconName={name} diameter="16px" color="currentColor" />
);

type CategoryKey = keyof Omit<
  BlueprintAspirationsCategories,
  'id' | 'career_user_response_set_id' | 'created_at' | 'updated_at'
>;

type CardConfig = Readonly<{
  key: CategoryKey;
  iconName: AppIconProps['iconName'];
  title: string;
}>;

const CARD_CONFIG: CardConfig[] = [
  { key: 'lifestyle_and_home', iconName: 'Home03', title: STRINGS.lifestyleAndHome },
  { key: 'work_settings', iconName: 'Building06', title: STRINGS.workSettings },
  { key: 'sources_of_meaning', iconName: 'Scales01', title: STRINGS.sourcesOfMeaning },
  { key: 'locations_and_options', iconName: 'Globe05', title: STRINGS.locationsAndOptions },
  { key: 'career_attributes', iconName: 'Paperclip', title: STRINGS.careerAttributes },
  { key: 'activities', iconName: 'GamingPad02', title: STRINGS.activities },
  { key: 'family_and_caregiving', iconName: 'Users02', title: STRINGS.familyAndCaregiving },
  { key: 'building_wealth', iconName: 'CoinsStacked02', title: STRINGS.buildingWealth },
];

function buildCards(categories?: BlueprintAspirationsCategories | null) {
  return CARD_CONFIG.map(({ key, iconName, title }) => ({
    key,
    icon: cardIcon(iconName),
    title,
    description: categories?.[key],
  }));
}

// --- Styles ---

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-3xl'],
  bgcolor: backrs.colors['Brand/25'],
  border: `1px solid ${backrs.colors['Colors/Foreground/fg-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  overflow: 'hidden',
  padding: backrs.spacing['spacing-2xl'],
  '@container (min-width: 600px)': {
    padding: backrs.spacing['spacing-3xl'],
  },
});

const headerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  flexWrap: 'wrap',
});

const profileRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-lg'],
});

const avatarSx: SxProps<Theme> = ({ backrs, typography }) => ({
  width: 64,
  height: 64,
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  outline: `2px solid ${backrs.colors['Brand/500']}`,
  outlineOffset: '3px',
  bgcolor: backrs.colors['Colors/Base/White'],
  color: backrs.colors['Colors/Text/text-primary'],
  ...typography['text-xl-semibold'],
});

const sectionTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const gridSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  display: 'grid',
  gap: backrs.spacing['spacing-xl'],
  marginTop: backrs.spacing['spacing-md'],
  gridTemplateColumns: '1fr',
  '@container (min-width: 500px)': {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@container (min-width: 700px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const lubavButtonWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  zIndex: 1,
  '@container (min-width: 700px)': {
    position: 'absolute',
    top: backrs.spacing['spacing-3xl'],
    right: backrs.spacing['spacing-3xl'],
  },
});
